import { useState } from "react";
import { Check, Copy, Download, Share2, Twitter, Eye, Heart, Instagram, Youtube, ExternalLink, Facebook, Globe } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { safeUrl } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Emoji flag by region key (kept minimal; falls back to text-only pill)
const REGION_FLAG = { US: "🇺🇸", UK: "🇬🇧", India: "🇮🇳", Global: "🌍" };

// Best-effort classification of the auto-attached social link
function classifyLink(url) {
  if (!url) return null;
  const u = url.trim();
  if (/x\.com|twitter\.com/i.test(u)) return "x";
  if (/instagram\.com/i.test(u)) return "instagram";
  if (/youtube\.com|youtu\.be/i.test(u)) return "youtube";
  if (/facebook\.com|fb\.com/i.test(u)) return "facebook";
  return "website";
}

export default function ShareDiscoverModal({ open, onOpenChange, post }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const ogUrl = `${BACKEND_URL}/api/discover/${post.id}/og`;
  const shareUrl = `${window.location.origin}/discover`;
  const twitterText = encodeURIComponent(`Check out ${post.business_name} on HighRank Discover 🚀`);
  const twitterUrl = `https://twitter.com/intent/tweet?text=${twitterText}&url=${encodeURIComponent(ogUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${post.business_name} — ${post.tagline || ""} ${ogUrl}`)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(ogUrl);
      setCopied(true);
      toast.success("Share link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy link");
    }
  };

  const downloadImage = async () => {
    setDownloading(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/discover/${post.id}/share-image`);
      if (!resp.ok) throw new Error("Image generation failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${post.business_name.replace(/\s+/g, "-").toLowerCase()}-highrank.png`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Image downloaded");
    } catch {
      toast.error("Could not generate image — try again");
    } finally {
      setDownloading(false);
    }
  };

  const nativeShare = async () => {
    try {
      await navigator.share({
        title: `${post.business_name} on HighRank Discover`,
        text: post.tagline || "",
        url: ogUrl,
      });
    } catch {
      // cancelled
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="discover-share-modal"
        className="border border-slate-200 sm:max-w-lg shadow-[0_24px_60px_rgba(0,0,0,0.10),0_4px_16px_rgba(0,0,0,0.06)] bg-white"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Share2 size={18} className="text-emerald-500" /> Share this card
          </DialogTitle>
          <DialogDescription className="text-slate-500 text-sm leading-relaxed">
            Share {post.business_name}'s Discover card — with a branded preview image for social media.
          </DialogDescription>
        </DialogHeader>

        {/* Preview card — light premium look mirroring the branded share image */}
        <div
          data-testid="discover-share-preview"
          className="relative overflow-hidden rounded-2xl border border-emerald-100"
          style={{
            background: "linear-gradient(140deg, #FFFFFF 0%, #F6FFFA 55%, #FFF9EE 100%)",
            minHeight: "220px",
            boxShadow: "0 12px 40px -12px rgba(16,185,129,0.20), 0 4px 14px rgba(0,0,0,0.05)",
          }}
        >
          {/* Emerald left accent bar */}
          <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-gradient-to-b from-emerald-400 via-emerald-500 to-emerald-400/60" />

          <div className="relative p-4 sm:p-5 flex gap-4">
            {/* Left: Business logo (falls back to thumbnail, then initial) */}
            {(() => {
              const logo = post.logo_url || post.thumbnail_url;
              if (logo) {
                return (
                  <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white flex items-center justify-center">
                    <img
                      src={logo}
                      alt={`${post.business_name} logo`}
                      className={post.logo_url ? "w-full h-full object-contain p-1.5" : "w-full h-full object-cover"}
                    />
                  </div>
                );
              }
              return (
                <div className="shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <span className="font-display text-3xl font-bold text-emerald-500">
                    {post.business_name?.[0]?.toUpperCase() || "H"}
                  </span>
                </div>
              );
            })()}

            {/* Right: Content */}
            <div className="min-w-0 flex-1 flex flex-col">
              {/* Top row: brand mark + region */}
              <div className="flex items-center justify-between gap-2">
                <p className="font-mono2 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-900 m-0">
                  HIGHRANK<span className="text-emerald-500">.space</span>
                </p>
                {post.region && (
                  <span
                    data-testid="discover-share-region"
                    className="shrink-0 inline-flex items-center gap-1 rounded-full border border-blue-300 bg-blue-50 px-2 py-0.5 font-mono2 text-[9px] font-bold uppercase tracking-[0.15em] text-blue-700"
                  >
                    {REGION_FLAG[post.region] && (
                      <span aria-hidden="true" className="text-[11px] leading-none">
                        {REGION_FLAG[post.region]}
                      </span>
                    )}
                    {post.region}
                  </span>
                )}
              </div>

              {/* Category */}
              {post.category && (
                <span
                  className="mt-1.5 inline-flex self-start items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 font-mono2 text-[9px] font-bold uppercase tracking-[0.12em] text-emerald-700"
                >
                  {post.category}
                </span>
              )}

              {/* Business name */}
              <h3 className="mt-2 font-heading text-base sm:text-lg font-bold text-slate-900 leading-tight truncate">
                {post.business_name}
              </h3>

              {/* Meta row: views · likes · social icons · view details */}
              <div className="mt-2.5 flex items-center flex-wrap gap-x-2.5 gap-y-1.5">
                <span
                  data-testid="discover-share-views"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600"
                >
                  <Eye size={12} className="text-slate-400" />
                  {post.view_count ?? 0}
                </span>
                <span
                  data-testid="discover-share-likes"
                  className="inline-flex items-center gap-1 text-[11px] font-medium text-rose-500"
                >
                  <Heart size={12} fill="currentColor" />
                  {post.like_count ?? 0}
                </span>

                {/* Social icons — order: X, Instagram, YouTube, Facebook */}
                {(() => {
                  const linkKind = classifyLink(post.instagram_link);
                  const xUrl = post.social_x || (linkKind === "x" ? post.instagram_link : "");
                  const igUrl = linkKind === "instagram" ? post.instagram_link : "";
                  const ytUrl = linkKind === "youtube" ? post.instagram_link : "";
                  const fbUrl = linkKind === "facebook" ? post.instagram_link : "";
                  const socials = [
                    { url: xUrl, Icon: Twitter, label: "X", cls: "text-slate-900 hover:bg-slate-50", testid: "discover-share-x" },
                    { url: igUrl, Icon: Instagram, label: "Instagram", cls: "text-pink-600 hover:bg-pink-50", testid: "discover-share-instagram" },
                    { url: ytUrl, Icon: Youtube, label: "YouTube", cls: "text-red-600 hover:bg-red-50", testid: "discover-share-youtube" },
                    { url: fbUrl, Icon: Facebook, label: "Facebook", cls: "text-blue-600 hover:bg-blue-50", testid: "discover-share-facebook" },
                  ].filter((s) => s.url);
                  return socials.map(({ url, Icon, label, cls, testid }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      data-testid={testid}
                      className={`inline-flex items-center justify-center w-6 h-6 rounded-full border border-slate-200 bg-white transition ${cls}`}
                    >
                      <Icon size={12} />
                    </a>
                  ));
                })()}

                <a
                  href={ogUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="discover-share-view-details"
                  className="ml-auto inline-flex items-center gap-1 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider transition"
                >
                  View details <ExternalLink size={10} />
                </a>
              </div>
            </div>
          </div>

          {/* Footer strip — business website (with fallback tag) */}
          <div className="border-t border-emerald-100/70 bg-white/40 px-4 sm:px-5 py-1.5 flex items-center gap-2">
            <div className="w-6 h-[2px] bg-emerald-400 rounded shrink-0" />
            {post.website ? (
              <a
                href={safeUrl(post.website)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="discover-share-website"
                className="inline-flex items-center gap-1 font-mono2 text-[10px] font-semibold text-slate-600 hover:text-emerald-600 transition truncate"
              >
                <Globe size={11} className="text-emerald-500 shrink-0" />
                <span className="truncate">
                  {post.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                </span>
              </a>
            ) : (
              <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-slate-400">
                Featured on HighRank Discover
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 flex-wrap mt-2">
          <button
            data-testid="discover-share-copy-btn"
            onClick={copyLink}
            className="inline-flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 transition-all hover:opacity-90 active:scale-95 bg-emerald-500 hover:bg-emerald-400 text-white"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy link"}
          </button>

          <button
            data-testid="discover-share-download-btn"
            onClick={downloadImage}
            disabled={downloading}
            className="inline-flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 transition-all active:scale-95 disabled:opacity-50 border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700"
          >
            <Download size={14} />
            {downloading ? "Generating…" : "Download image"}
          </button>

          <a
            data-testid="discover-share-twitter-btn"
            href={twitterUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 transition-all hover:bg-slate-100 border border-slate-200 bg-slate-50 text-slate-700"
          >
            <Twitter size={14} /> X / Twitter
          </a>

          <a
            data-testid="discover-share-whatsapp-btn"
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 transition-all hover:bg-slate-100 border border-slate-200 bg-slate-50 text-slate-700"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>

          {"share" in navigator && (
            <button
              data-testid="discover-share-native-btn"
              onClick={nativeShare}
              className="inline-flex items-center gap-2 font-bold text-sm rounded-full px-4 py-2.5 transition-all hover:bg-slate-100 border border-slate-200 bg-slate-50 text-slate-700"
            >
              <Share2 size={14} /> Share
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
